import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Minus, Phone, Plus, X } from "lucide-react";
import { SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import smartlyMainLogo from "@/assets/smartly-main-logo.svg";
import { services } from "@/data/services";
import type { DbMenuItem } from "@/hooks/useMenuItems";

interface Child { label: string; url: string }
interface Row { id: string; label: string; url: string; children: Child[] }

interface Props {
  menuItems: DbMenuItem[] | undefined;
  brandName: string;
  phone: string;
  phoneHref: string;
  loginLabel: string;
  loginUrl: string;
  ctaLabel: string;
  ctaLink: string;
  onNavigate: () => void;
  onHomeClick: (e: React.MouseEvent) => void;
}

const allServices: Child[] = [
  { label: "All services", url: "/#services" },
  ...services.map((s) => ({ label: s.title, url: `/services/${s.slug}` })),
];

const fallbackRows: Row[] = [
  { id: "home", label: "Home", url: "/", children: [] },
  { id: "about", label: "About Us", url: "/about-us", children: [] },
  { id: "industries", label: "Who We Help", url: "/industries", children: [] },
  { id: "services", label: "Services", url: "#", children: allServices },
  { id: "contact", label: "Contact Us", url: "/contact", children: [] },
];

function buildRows(items: DbMenuItem[] | undefined): Row[] {
  if (!items || items.length === 0) return fallbackRows;
  const top = items.filter((i) => !i.parent_id).sort((a, b) => a.sort_order - b.sort_order);
  return top.map((t) => {
    const kids = items
      .filter((i) => i.parent_id === t.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((k) => ({ label: k.label, url: k.url }));
    let children = kids;
    if (t.url === "#" && kids.length === 0) children = allServices;
    else if (kids.some((k) => k.url.startsWith("/services/")))
      children = [{ label: "All services", url: "/#services" }, ...kids];
    else if (kids.length > 0 && t.url && t.url !== "#")
      children = [{ label: `All of ${t.label}`, url: t.url }, ...kids];
    return { id: t.id, label: t.label, url: t.url, children };
  });
}

const MobileMenu = ({
  menuItems, brandName, phone, phoneHref, loginLabel, loginUrl, ctaLabel, ctaLink, onNavigate, onHomeClick,
}: Props) => {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<string | null>(null);
  const rows = buildRows(menuItems);
  const isActive = (url: string) => (url === "/" ? pathname === "/" : url !== "#" && pathname.startsWith(url));

  return (
    <div className="mobile-menu-glow relative flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 px-4 pt-5 pb-4 sm:px-5">
        <SheetTitle asChild>
          <Link to="/" onClick={(e) => { onHomeClick(e); onNavigate(); }} className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
            <img src={smartlyMainLogo} alt="Smartly" className="h-[clamp(18px,6vw,28px)] w-auto shrink-0" width={96} height={28} />
            <span className="font-display text-[clamp(15px,5.2vw,20px)] font-bold tracking-tight text-foreground">{brandName}</span>
          </Link>
        </SheetTitle>
        <SheetDescription className="sr-only">Site navigation</SheetDescription>
        <SheetClose
          aria-label="Close menu"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-5 w-5" />
        </SheetClose>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-6">
        <a
          href={`tel:${phoneHref}`}
          onClick={onNavigate}
          className="flex items-center gap-4 rounded-2xl border border-primary/70 bg-card/40 p-4 transition-colors hover:bg-primary/10"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Phone className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm text-muted-foreground">Call us now</span>
            <span className="block font-display text-2xl font-bold tracking-tight text-foreground">{phone}</span>
          </span>
        </a>

        <nav aria-label="Mobile" className="mt-4">
          <ul>
            {rows.map((row) => {
              const hasKids = row.children.length > 0;
              const open = expanded === row.id;
              const active = isActive(row.url) || row.children.some((c) => isActive(c.url) && c.url !== "/#services");
              const labelCls = `font-display text-[28px] font-semibold leading-tight tracking-tight transition-colors ${active ? "text-primary" : "text-foreground"}`;
              return (
                <li key={row.id} className="border-b border-border">
                  {hasKids ? (
                    <>
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setExpanded(open ? null : row.id)}
                        className="flex min-h-[64px] w-full items-center justify-between py-3 text-left"
                      >
                        <span className={labelCls}>{row.label}</span>
                        {open ? <Minus className="h-5 w-5 text-primary" /> : <Plus className="h-5 w-5 text-foreground" />}
                      </button>
                      {open && (
                        <ul className="mb-3 flex flex-col animate-fade-in motion-reduce:animate-none">
                          {row.children.map((c) => (
                            <li key={c.url + c.label}>
                              <Link
                                to={c.url}
                                onClick={onNavigate}
                                className={`flex min-h-[44px] items-center justify-between py-2 pl-1 text-base transition-colors hover:text-primary ${isActive(c.url) && c.url !== "/#services" ? "text-primary" : "text-muted-foreground"}`}
                              >
                                {c.label}
                                <ChevronRight className="h-4 w-4 opacity-60" />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={row.url}
                      onClick={(e) => { if (row.url === "/") onHomeClick(e); onNavigate(); }}
                      aria-current={active ? "page" : undefined}
                      className="flex min-h-[64px] items-center justify-between py-3"
                    >
                      <span className={labelCls}>{row.label}</span>
                      <ChevronRight className="h-5 w-5 text-foreground" />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-border bg-background/90 px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur">
        <a
          href={loginUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          className="flex h-12 items-center justify-center rounded-full border border-primary px-4 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10"
        >
          {loginLabel}
        </a>
        <Link
          to={ctaLink || "/consultation"}
          onClick={onNavigate}
          className="flex h-12 items-center justify-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  );
};

export default MobileMenu;
